import { FeaturedCharities } from '@components/featured-charities';
import { Collection, CollectionTypeEnum } from '@givz/core-client';
import { useData } from '@hooks/form';
import { useNotificationChannel } from '@hooks/notification';
import { LoadingButton } from '@mui/lab';
import { Stack } from '@mui/material';
import { useGetCollection } from '@queries/collections';
import {
  useGetIncentive,
  useUpdateCollectionFromIncentive,
} from '@queries/incentives';
import { FunctionComponent } from 'react';
import { IncentiveWizardState } from '../create-incentive-wizard';

export type EditFeaturedCharities = {
  gid: string;
};

type FeaturedCharitiesState = Pick<
  IncentiveWizardState,
  'featuredCharitiesType' | 'selectedCharities'
>;

const isStateEqualToCollection = (
  state: FeaturedCharitiesState,
  collection?: Collection,
) => {
  let isEqual = Boolean(
    collection && state.featuredCharitiesType === collection.type,
  );

  if (
    isEqual &&
    state.featuredCharitiesType === CollectionTypeEnum.Handpicked
  ) {
    isEqual = Boolean(
      collection?.organizations.length === state.selectedCharities.size &&
        collection?.organizations.every(({ gid }) =>
          state.selectedCharities.has(gid),
        ),
    );
  }

  return isEqual;
};

const getStateFromCollection = (
  collection?: Collection,
): FeaturedCharitiesState => ({
  featuredCharitiesType: collection?.type ?? CollectionTypeEnum.Curated,
  selectedCharities: new Map(
    collection?.organizations.map((organization) => [
      organization.gid,
      organization,
    ]) ?? [],
  ),
});

export const EditFeaturedCharities: FunctionComponent<EditFeaturedCharities> = (
  props,
) => {
  const { gid } = props;
  const { success } = useNotificationChannel();
  const getIncentiveQuery = useGetIncentive({ body: {}, gid });
  const getCollectionQuery = useGetCollection(
    {
      gid: getIncentiveQuery.data?.data?.collection.gid!,
    },
    {
      enabled: Boolean(getIncentiveQuery.data?.data?.collection.gid),
    },
  );
  const collectionData = getCollectionQuery.data?.data;
  const { isLoading, updateCollection } = useUpdateCollectionFromIncentive(gid);

  const { data, setData, setValueFromKey } = useData<FeaturedCharitiesState>(
    getStateFromCollection(collectionData),
  );

  function onSuccess() {
    success(`The incentive ${gid} was updated.`);
  }

  function computeDataFromType(featuredCharitiesType: CollectionTypeEnum) {
    if (collectionData) {
      setData({
        featuredCharitiesType,
        selectedCharities:
          featuredCharitiesType === CollectionTypeEnum.Handpicked &&
          collectionData?.type === CollectionTypeEnum.Handpicked
            ? new Map(
                collectionData?.organizations.map((organization) => [
                  organization.gid,
                  organization,
                ]),
              )
            : new Map(),
      });
    }
  }

  const onSave = () => {
    updateCollection({
      collectionType: data.featuredCharitiesType,
      onUpdated: onSuccess,
      organizationGids: Array.from(data.selectedCharities.keys()),
    });
  };

  const disableSave = isStateEqualToCollection(data, collectionData);

  return (
    <Stack spacing={2}>
      <FeaturedCharities
        type={data.featuredCharitiesType}
        onChangeType={computeDataFromType}
        onChangeSelectedCharities={setValueFromKey('selectedCharities')}
        selectedCharities={data.selectedCharities}
      />
      <Stack direction="row" flexDirection="row-reverse">
        <LoadingButton
          loading={isLoading}
          onClick={onSave}
          disabled={isLoading || disableSave}
        >
          Save
        </LoadingButton>
      </Stack>
    </Stack>
  );
};
